import { Component, ElementRef, input, Input, OnInit, output, viewChild } from '@angular/core';
import {AsyncPipe, CommonModule, JsonPipe} from '@angular/common';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { debounceTime, map, Observable, startWith, Subject, switchMap, throttleTime, merge } from 'rxjs';
import { FetchSuggestionsFn } from './fetch-suggestions.function';
import { HasId } from '../../../model/has-id.model';
import { CantaaErrorHandlerService } from '../../../service/cantaa-error-handler.service';
import { SelectedValidator } from '../../../validator/selected.validator';

@Component({
  selector: 'wim-suggestion-input',
  standalone: true,
  imports: [
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatAutocompleteModule,
    ReactiveFormsModule,
    AsyncPipe,
    JsonPipe,
    CommonModule
  ],
  templateUrl: './suggestion-input.component.html',
  styleUrl: './suggestion-input.component.scss'
})

// debounceTime: Emits the last value in the sequence after a specified pause between values.
//   It is useful for scenarios where you want to wait for a pause before taking action.
//
// throttleTime: Emits the first value immediately and then ignores subsequent values for a specified duration.
//   It is useful for rate-limiting the emission of values to avoid overwhelming systems.

export class SuggestionInputComponent<T extends HasId> implements OnInit {
  @Input() fetchSuggestions!: FetchSuggestionsFn<T>;
  @Input() displayFn!: (option: T) => string;
  @Input() optional = false;
  @Input() fetchSuggestionsOnFocus = false;
  @Input() allowShortQueries = false;
  label = input.required<string>();

  // only for focus
  input = viewChild.required<ElementRef>('suggestionInput');

  // need for throttling
  private internalEmitter = new Subject<any>();
  // needed to trigger search without value change
  private searchEmitter = new Subject<any>();
  onOptionSelected = output<T | null>();

  searchControl = new FormControl<string | T>('', this.optional ? null : SelectedValidator);
  filteredOptions?: Observable<T[]>;

  constructor(private errorHandler: CantaaErrorHandlerService,
              private el: ElementRef) {
  }

  ngOnInit() {

    this.filteredOptions = merge(
      this.searchControl.valueChanges.pipe(
        map(value => {
          const query = typeof value === 'string'
            ? value
            : value?.id.toString() ?? '';

          console.log('SUGGESTION QUERY:', query);

          return query;
        })
      ),
      this.searchEmitter
    ).pipe(
      debounceTime(200),
      switchMap(query => {
        console.log('Fetching suggestions:', query);
        return this.fetchSuggestionsInternal(query);
      })
    );

    this.searchControl.valueChanges.subscribe(value => {
      if (
        this.optional &&
        typeof value === 'string' &&
        value.trim().length === 0
      ) {
        this.internalEmitter.next(null);
      }
    });

    this.internalEmitter.pipe(
      throttleTime(2000) // Throttle in milliseconds, ensures that the event is emitted at most once during period
    ).subscribe(value => {
      this.onOptionSelected.emit(value);
    });
  }

  async fetchSuggestionsInternal(query: string) {
    if (!this.allowShortQueries && query.length < 3) {
      return [];
    }

    try {
      return await this.fetchSuggestions(query);
    } catch (e) {
      this.errorHandler.handleError(e, 'FAILED_TO_FETCH');
      return [];
    }
  }

  onOptionSelectedInternal($event: MatAutocompleteSelectedEvent) {
    const value = $event.option.value;

    console.log('AUTOCOMPLETE SELECTED:', value);

    if (value?.id === -1) {
      console.log('EMITTING NULL');
      this.internalEmitter.next(null);
    } else {
      this.internalEmitter.next(value);
    }
  }

  clear(): void {
    this.searchControl.reset();
    this.searchControl.setValue('');
  }

  enable(): void {
    this.searchControl.enable({ emitEvent: false });
  }

  disable(): void {
    this.searchControl.disable({ emitEvent: false });
  }

  focus(): void {
    // FocusHelper.focusElementByClass(this.el, 'suggestionInput');
    // this.myInput.nativeElement.focus();
    setTimeout(() => this.input().nativeElement.focus(), 50);
  }

  setValue(value: any): void {
    this.searchControl.setValue(value);
  }

  enterOnEmpty = output<void>();

  onEnter(event: Event) {
    event.preventDefault();
    event.stopPropagation();

    const value = this.searchControl.value;

    if (this.optional && (value === null || value === '')) {
      this.internalEmitter.next(null);
      this.enterOnEmpty.emit();
    } else if (value && typeof value !== 'string') {
      this.internalEmitter.next(value);
    }
  }

  onTab() {
    const value = this.searchControl.value;
    if (this.optional && !value) {
      this.internalEmitter.next(value);
    } else if (value && typeof value !== 'string') {
      this.internalEmitter.next(value);
    }
  }

  get value(): string | T | null {
    return this.searchControl.value;
  }

  get selectedValue(): T | null {
    const value = this.searchControl.value;
    return value && typeof value !== 'string' ? value : null;
  }

  triggerSearch(): void {
    this.searchEmitter.next('');
  }
}
